/**
 * TextField Component
 *
 * Tyler Forge text-field adapter for React Hook Form
 */

import { forwardRef } from 'react';
import { type FieldError } from 'react-hook-form';
import '@tylertech/forge/text-field';
import './TextField.css';

export interface TextFieldProps {
  /** Field name (required for React Hook Form) */
  name: string;
  /** Field label */
  label?: string;
  /** Field type (text, email, password, etc.) */
  type?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Is field required */
  required?: boolean;
  /** Is field disabled */
  disabled?: boolean;
  /** Helper text */
  helperText?: string;
  /** Validation error from React Hook Form */
  error?: FieldError;
  /** Default value */
  defaultValue?: string;
  /** Current value (controlled) */
  value?: string;
  /** Change handler */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Blur handler */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Text field adapter for React Hook Form
 *
 * Wraps Tyler Forge text-field component with React Hook Form support
 *
 * @example
 * ```tsx
 * <TextField
 *   label="Service Name"
 *   {...register('name', { required: 'Name is required' })}
 *   error={errors.name}
 * />
 * ```
 */
const ForgeTextField = 'forge-text-field' as React.ElementType;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      name,
      label,
      type = 'text',
      placeholder,
      required = false,
      disabled = false,
      helperText,
      error,
      defaultValue,
      value,
      onChange,
      onBlur,
      className,
    },
    ref
  ) => {
    const hasError = Boolean(error);
    const errorId = hasError ? `${name}-error` : undefined;

    return (
      <div className={`text-field-wrapper ${className || ''}`}>
        <ForgeTextField invalid={hasError || undefined} disabled={disabled || undefined}>
          {label && (
            <label htmlFor={name} className="form-label">
              {label}
              {required && (
                <span className="form-required" aria-hidden="true">
                  *
                </span>
              )}
            </label>
          )}
          <input
            ref={ref}
            id={name}
            name={name}
            type={type}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            onBlur={onBlur}
            aria-required={required || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={errorId}
          />
          {hasError && error?.message && (
            <div id={errorId} slot="support-text" className="form-error-message" role="alert">
              {error.message}
            </div>
          )}
          {!hasError && helperText && (
            <div slot="support-text" className="form-helper-text">
              {helperText}
            </div>
          )}
        </ForgeTextField>
      </div>
    );
  }
);

TextField.displayName = 'TextField';
